import { ccfRoutes } from '@app/ccf/ccf-routes';
import { useCCFUpsellDialog } from '@app/hooks/use-ccf-upsell-dialog';
import { CompanyReportCategoryStatusStatusEnum } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import GuideTemplate from '@shared/components/guide-template';
import { Card, CardHeader } from '@shared/components/ui/card';
import { useToast } from '@shared/components/ui/use-toast';
import { useFormContext } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { useCCF } from '../ccf-context';
import { useProcessScopes } from '../scopes';
import { ScopeReviewRow } from './components/scope-review-row';

function Scope3NotSupportedRow({
  icon,
  name,
}: {
  icon: React.ReactNode;
  name: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3">
        <div>{icon}</div>
        <span className="ml-2 text-md">{name}</span>
        <div className="ml-auto flex items-baseline">
          <span className="flex items-center">
            <Icons.x className="mr-1 text-gray-400" size={14} />
            <Text variant="subtle">Not included</Text>
          </span>
        </div>
      </CardHeader>
    </Card>
  );
}

export default function ReportReviewScope3() {
  const navigate = useNavigate();
  const { uuid: reportUuid, scope3Supported, hasUnreviewedData } = useCCF();
  const { showDialog, UpsellDialogComponent } = useCCFUpsellDialog();
  const { scope3 } = useProcessScopes();
  const { trigger } = useFormContext();
  const { toast } = useToast();

  const validateScope3 = async () => {
    const isValid = await trigger();

    if (!isValid) {
      toast({
        title: 'Please ensure all fields are filled',
        variant: 'destructive',
      });
    }
  };

  const scope3Upgrade = {
    title: 'Upgrade to include Scope 3',
    description: (
      <div className="space-y-2">
        <Text>
          Your report won’t include any of the following Scope 3 categories,
          which can account for around 90% of total company emissions for a
          business like yours.
        </Text>
        <Text>
          <span onClick={showDialog} className="cursor-pointer text-teal">
            Upgrade
          </span>{' '}
          to include Scope 3 in your report today.{' '}
        </Text>
      </div>
    ),
  };
  return (
    <GuideTemplate
      preTitle=""
      title={!scope3Supported ? scope3Upgrade.title : 'Review Scope 3'}
      description={
        !scope3Supported
          ? scope3Upgrade.description
          : 'For each category, if there is no data please select a reason from the dropdown. For categories with data, you should check that all the relevant data has been included.'
      }
      primaryActionText="Publish"
      primaryActionProps={{
        onClick: () => validateScope3(),
        type: 'submit',
        'data-testid': 'submit-publish-button',
      }}
      secondaryActionText="Back"
      secondaryActionProps={{
        onClick: () => {
          navigate(
            generatePath(ccfRoutes.PUBLISH_REVIEW_SCOPE_2, {
              report_uuid: reportUuid,
            })
          );
        },
      }}
    >
      <div className="w-[1000px] max-w-lg space-y-6">
        {scope3
          .filter(
            (item) =>
              item.status !== CompanyReportCategoryStatusStatusEnum.not_required
          )
          .map((item) => {
            if (item.disabled || !item.isSupported) {
              return null;
            }
            if (!scope3Supported) {
              return (
                <Scope3NotSupportedRow
                  key={item.slug}
                  icon={item.icon}
                  name={item.name}
                />
              );
            }

            return (
              <ScopeReviewRow
                key={item.slug}
                icon={item.icon}
                name={item.name}
                description={item.description}
                slug={item.slug}
                status={item.status!}
                hasActivityData={item.hasActivityData!}
                isSupported={item.isSupported}
                hasUnreviewedData={hasUnreviewedData}
              />
            );
          })}
      </div>
      {!scope3Supported && UpsellDialogComponent}
    </GuideTemplate>
  );
}
